import React from "react";
import { AppProps } from "next/app";
import { Provider } from "react-redux";
import { newBcaTheme, BCAThemeProvider, SSRProvider } from "@bca/ui-library";
import { createGlobalStyle } from "styled-components";

import { store } from "../src/redux/store";
import ErrorPage from "../src/ErrorPage";
import AppInsightsService from "../src/services/AppInsightsService";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { AuthProvider } from "@bca-auth0/bca-auth0-provider";
import { AuthZeroContextProvider } from "../src/context/AuthZeroContext";
import Head from "next/head";
import { ErrorProvider } from "../src/context/ErrorContext";

const GlobalStyle = createGlobalStyle`
  div#__next, html, body {
    margin: 0;
    height: 100%;
    min-height: 100%;
    min-width: 100%;
    font-family: "Roboto", "sans-serif";
    position: relative;
  }
`;

interface MyPurchasesProps extends AppProps {
  pageProps: {
    user: { firstName: string; lastName: string };
    idToken: string;
    redirectUri: string;
    auth0Audience: string;
    auth0ClientId: string;
    auth0Domain: string;
    auth0Scope: string;
  };
  appInsightsConnectionString: string;
}

export default function MyPurchases({
  Component,
  pageProps,
  appInsightsConnectionString,
}: MyPurchasesProps) {
  const {
    user,
    idToken,
    redirectUri,
    auth0Audience,
    auth0ClientId,
    auth0Domain,
    auth0Scope,
  } = pageProps;

  const appInsightsInstance = AppInsightsService.getInstance(
    appInsightsConnectionString
  );
  const reactPlugin = appInsightsInstance.reactPlugin;

  console.log(
    "auth0 common vars: ",
    auth0Audience,
    auth0ClientId,
    auth0Domain,
    auth0Scope
  );

  return (
    <SSRProvider>
      <AuthProvider
        domain={auth0Domain}
        clientId={auth0ClientId}
        useRefreshTokens
        authorizationParams={{
          redirect_uri: redirectUri,
          audience: auth0Audience,
          scope: auth0Scope,
        }}
      >
        <Head>
          <meta
            name="viewport"
            content="initial-scale=1.0, width=device-width"
          />
        </Head>
        <GlobalStyle />
        <BCAThemeProvider theme={newBcaTheme}>
          <Provider store={store}>
            <ErrorProvider>
              <AuthZeroContextProvider>
                <AppInsightsErrorBoundary
                  onError={() => <ErrorPage user={user} idToken={idToken} />}
                  appInsights={reactPlugin}
                >
                  <Component {...pageProps} />
                </AppInsightsErrorBoundary>
              </AuthZeroContextProvider>
            </ErrorProvider>
          </Provider>
        </BCAThemeProvider>
      </AuthProvider>
    </SSRProvider>
  );
}

MyPurchases.getInitialProps = async () => {
  return {
    pageProps: {
      redirectUri: process.env.AUTH_PROVIDER_REDIRECT_URI,
      auth0Audience: process.env.AUTH0_AUDIENCE,
      auth0ClientId: process.env.AUTH0_CLIENT_ID,
      auth0Domain: process.env.AUTH0_DOMAIN,
      auth0Scope: process.env.AUTH0_SCOPE,
    },
    appInsightsConnectionString:
      process.env.APPLICATIONINSIGHTS_CONNECTION_STRING,
  };
};
